import { createTheme, alpha } from '@mui/material/styles';
import { brand, neutral, semantic, radius, elevation, typography } from './tokens';

// RC18.2 — Enterprise Design System theme factory. `mode` is 'light' | 'dark';
// `compact` shrinks control heights/paddings for dense data-entry screens (Phase 10).
export function buildTheme(mode = 'light', compact = false) {
  const isDark = mode === 'dark';

  const background = isDark
    ? { default: neutral[950], paper: neutral[900] }
    : { default: neutral[50], paper: neutral[0] };

  const text = isDark
    ? { primary: neutral[100], secondary: neutral[400], disabled: neutral[600] }
    : { primary: neutral[900], secondary: neutral[600], disabled: neutral[400] };

  const divider = isDark ? alpha(neutral[300], 0.12) : neutral[200];

  const dense = compact
    ? { control: 32, controlPad: '4px 10px', rowPad: '6px 12px' }
    : { control: 40, controlPad: '8px 14px', rowPad: '10px 16px' };

  return createTheme({
    palette: {
      mode,
      primary: { main: brand[500], light: brand[300], dark: brand[700], contrastText: '#fff' },
      secondary: { main: neutral[700], light: neutral[500], dark: neutral[900], contrastText: '#fff' },
      success: { main: semantic.success[500], light: semantic.success[400], dark: semantic.success[700] },
      warning: { main: semantic.warning[500], light: semantic.warning[400], dark: semantic.warning[700] },
      error: { main: semantic.error[500], light: semantic.error[400], dark: semantic.error[700] },
      info: { main: semantic.info[500], light: semantic.info[400], dark: semantic.info[700] },
      background,
      text,
      divider,
      grey: neutral,
    },
    shape: { borderRadius: radius.md },
    spacing: 8,
    typography: {
      fontFamily: typography.fontFamily,
      h1: { fontSize: typography.scale.display, fontWeight: typography.weight.semibold, letterSpacing: -0.5 },
      h2: { fontSize: typography.scale.xxl, fontWeight: typography.weight.semibold, letterSpacing: -0.25 },
      h3: { fontSize: typography.scale.xl, fontWeight: typography.weight.semibold },
      h4: { fontSize: typography.scale.xl, fontWeight: typography.weight.semibold },
      h5: { fontSize: typography.scale.lg, fontWeight: typography.weight.semibold },
      h6: { fontSize: typography.scale.md, fontWeight: typography.weight.semibold },
      subtitle1: { fontSize: typography.scale.md, fontWeight: typography.weight.medium },
      subtitle2: { fontSize: typography.scale.sm, fontWeight: typography.weight.medium },
      body1: { fontSize: typography.scale.body },
      body2: { fontSize: typography.scale.sm },
      caption: { fontSize: typography.scale.xs },
      button: { fontWeight: typography.weight.semibold, textTransform: 'none' },
      overline: { fontSize: typography.scale.xs, fontWeight: typography.weight.semibold, letterSpacing: 0.6 },
    },
    shadows: [
      'none',
      elevation[1], elevation[1],
      elevation[2], elevation[2], elevation[2],
      elevation[3], elevation[3], elevation[3], elevation[3],
      elevation[4], elevation[4], elevation[4], elevation[4],
      // MUI requires 25 entries; repeat the top tier for the remaining unused indices.
      ...Array(11).fill(elevation[4]),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { scrollbarColor: `${isDark ? neutral[600] : neutral[300]} transparent` },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: radius.sm, padding: dense.controlPad, fontWeight: typography.weight.semibold },
          sizeSmall: { padding: '4px 10px' },
        },
      },
      MuiTextField: {
        defaultProps: { size: compact ? 'small' : 'medium' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: radius.sm },
          input: { padding: dense.controlPad },
        },
      },
      MuiSelect: {
        defaultProps: { size: compact ? 'small' : 'medium' },
      },
      MuiCheckbox: {
        styleOverrides: { root: { padding: 6 } },
      },
      MuiSwitch: {
        styleOverrides: { root: { padding: 8 } },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: dense.control },
          indicator: { height: 2.5, borderRadius: radius.pill },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: dense.control,
            textTransform: 'none',
            fontWeight: typography.weight.semibold,
            fontSize: typography.scale.sm,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius.pill, fontWeight: typography.weight.medium },
          sizeSmall: { fontSize: typography.scale.xs },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius.lg,
            border: `1px solid ${divider}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: radius.md },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: radius.lg },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { padding: dense.rowPad, borderBottomColor: divider },
          head: {
            fontWeight: typography.weight.semibold,
            fontSize: typography.scale.xs,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            color: text.secondary,
            backgroundColor: isDark ? neutral[900] : neutral[50],
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? neutral[700] : neutral[800],
            fontSize: typography.scale.xs,
            borderRadius: radius.sm,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: { borderRadius: radius.sm },
        },
      },
    },
  });
}

export default buildTheme;
